// §7.4 step 4 / §18 F1 — when sync runs, and what the shell says while it cannot.
//
// The protocol is `sync-client.ts`; this is the thing that decides *when* to invoke it and holds
// the one piece of state the tray reads. Keeping them apart is what let M12.7 test convergence with
// two clients and no timers at all.
//
// §7.4's four triggers, and why each is there:
//
//   • **on load** — a device that was off overnight is behind before anyone touches it.
//   • **on `online`** — the moment F1's outage ends. This is the one M12.10 is named for.
//   • **every 60 s while visible** — another device may have written; nothing tells us but time.
//     Gated on visibility because a background tab polling forever is a battery bug.
//   • **2 s after the last local write** — debounced, so saving a file forty times in a minute is
//     one sync and not forty.
//
// **Guests never sync** (§7.4). The service exists for them but stays `disabled`, which is what
// keeps the tray quiet rather than showing an offline warning to somebody who has no server.
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { SyncClient, SyncConflict } from './sync-client';

/** §7.4 — every 60 s while visible. */
export const SYNC_INTERVAL_MS = 60_000;
/** §7.4 — 2 s after the last local write. */
export const SYNC_WRITE_DEBOUNCE_MS = 2_000;

export type SyncStatus =
  /** A guest, or nobody signed in: §7.4 says there is nothing to sync. */
  | 'disabled'
  /** Signed in, up to date. */
  | 'idle'
  /** A run is in flight. */
  | 'syncing'
  /** §18 F1 — signed in and the server cannot be reached. The tray says so. */
  | 'offline';

export interface SyncState {
  readonly status: SyncStatus;
  /** Epoch ms of the last run that completed without a transport failure. */
  readonly lastSyncedAt: number | null;
  /** D-045 — conflicts raised by the most recent run, for §13's notification. */
  readonly conflicts: readonly SyncConflict[];
  /** How many runs have completed. The e2e waits on this rather than on a timer. */
  readonly runs: number;
}

export interface SyncService {
  readonly store: StoreApi<SyncState>;
  /** Turn sync on for a signed-in session. Idempotent. */
  enable(client: SyncClient): void;
  /** Back to `disabled` — a sign-out, or a session that turned out not to exist. */
  disable(): void;
  /** Start listening for §7.4's triggers. Returns the detach. */
  attach(target?: Window | undefined): () => void;
  /** §7.4 — "2 s after the last local write". Called by the VFS on every write. */
  noteLocalWrite(): void;
  /** Run now. Resolves when the run finishes, whether or not it succeeded. */
  run(): Promise<void>;
}

export interface SyncServiceOptions {
  readonly now?: () => number;
  /** §13 — a conflict copy is announced. The kernel supplies the notifier. */
  readonly onConflicts?: (conflicts: readonly SyncConflict[]) => void;
}

export function createSyncService(options: SyncServiceOptions = {}): SyncService {
  const now = options.now ?? (() => Date.now());
  const store = createStore<SyncState>(() => ({
    status: 'disabled',
    lastSyncedAt: null,
    conflicts: [],
    runs: 0,
  }));

  let client: SyncClient | null = null;
  let inFlight: Promise<void> | null = null;
  let writeTimer: ReturnType<typeof setTimeout> | null = null;
  let interval: ReturnType<typeof setInterval> | null = null;

  /**
   * `navigator.onLine` is famously optimistic — it reports the network interface, not whether the
   * server answers. It is used only as a *hint* for the offline state; the authority is whether a
   * run actually completed, which is why `run()` sets the status from its own outcome.
   */
  const seemsOnline = (): boolean =>
    typeof navigator === 'undefined' || navigator.onLine !== false;

  async function run(): Promise<void> {
    if (!client) return;
    // One run at a time. A second trigger during a run joins the one already going rather than
    // starting a race between two clients over the same local files.
    if (inFlight) return inFlight;

    store.setState({ status: 'syncing' });
    inFlight = (async () => {
      try {
        const result = await client.sync();
        store.setState((state) => ({
          status: 'idle',
          lastSyncedAt: now(),
          conflicts: result.conflicts,
          runs: state.runs + 1,
        }));
        if (result.conflicts.length > 0) options.onConflicts?.(result.conflicts);
      } catch {
        // §18 F1 — "Desktop keeps working against OPFS". A failed sync is not an error the user
        // has to deal with; it is a state the tray reports and the next trigger retries.
        store.setState((state) => ({ ...state, status: 'offline' }));
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  return {
    store,

    enable(next) {
      client = next;
      store.setState((state) => ({
        ...state,
        status: seemsOnline() ? 'idle' : 'offline',
      }));
    },

    disable() {
      client = null;
      if (writeTimer) clearTimeout(writeTimer);
      writeTimer = null;
      store.setState({ status: 'disabled', lastSyncedAt: null, conflicts: [], runs: 0 });
    },

    noteLocalWrite() {
      if (!client) return;
      if (writeTimer) clearTimeout(writeTimer);
      writeTimer = setTimeout(() => {
        writeTimer = null;
        void run();
      }, SYNC_WRITE_DEBOUNCE_MS);
    },

    run,

    attach(target = typeof window === 'undefined' ? undefined : window) {
      if (!target) return () => undefined;

      const onOnline = () => {
        if (!client) return;
        // The moment F1's outage ends. Not "mark online and wait for the timer" — the point of
        // the event is that the user's unsynced work goes up now.
        void run();
      };
      const onOffline = () => {
        if (client) store.setState((state) => ({ ...state, status: 'offline' }));
      };
      const onVisible = () => {
        if (client && target.document.visibilityState === 'visible') void run();
      };

      target.addEventListener('online', onOnline);
      target.addEventListener('offline', onOffline);
      target.document.addEventListener('visibilitychange', onVisible);

      interval = setInterval(() => {
        if (!client) return;
        // §7.4 — "while visible". A hidden tab syncs when it comes back, on `visibilitychange`.
        if (target.document.visibilityState === 'visible') void run();
      }, SYNC_INTERVAL_MS);

      // §7.4 — "on load".
      if (client) void run();

      return () => {
        target.removeEventListener('online', onOnline);
        target.removeEventListener('offline', onOffline);
        target.document.removeEventListener('visibilitychange', onVisible);
        if (interval) clearInterval(interval);
        interval = null;
        if (writeTimer) clearTimeout(writeTimer);
        writeTimer = null;
      };
    },
  };
}
